import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { ResponsePostLogin } from "../../@types/fun-to-wait";
import { renewSession } from "./login.api";
import { authorizationItem, cleanToken, haveSession, setToken } from "@/compositions/auth";
import i18n from "@/i18n";
import router from "@/router";

const http: AxiosInstance = axios.create({
    baseURL: process.env.VUE_APP_ADAPTER_API,
    timeout: 10 * 1000
});

http.interceptors.request.use(
    (config: AxiosRequestConfig) => {
        if (haveSession()) {
            config.headers.common["Authorization"] = authorizationItem();
        }
        // i18n lang param
        config.params = {
            ...config.params,
            lang: i18n.global.locale.value
        };

        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error.response);
    }
);

// avoid pushing to /login more than once if several requests 401 at the same time
let isRedirectingToLogin = false;

const redirectToLogin = () => {
    if (isRedirectingToLogin) return;
    isRedirectingToLogin = true;

    cleanToken();
    router.push({
        path: "/login",
        query: { redirect: router.currentRoute.value.fullPath, sessionExpired: "1" }
    });
};

// Check accesstoken validity if not try to refresh tokens
http.interceptors.response.use(
    async (response: AxiosResponse): Promise<AxiosResponse> => {
        return response;
    },
    async (error: AxiosError) => {
        const originalRequest: AxiosRequestConfig = error.config;
        // FIX: take off when Cognito issue gets fixed
        // Cognito needs token even logout, that is weird
        if (originalRequest.url?.includes("/logout") || originalRequest.url?.includes("/reset-password")) {
            return Promise.resolve(error.response);
        } else if (originalRequest.url?.includes("/user/session")) {
            // the refresh-token request itself failed (refresh token expired/invalid) -
            // must not retry via renewSession() again here, or it recurses into itself forever
            redirectToLogin();
            return Promise.reject(error);
        } else if (error.response !== undefined && error.response.status === 401) {
            if (!haveSession()) {
                return Promise.reject(error);
            }

            const loginResult: ResponsePostLogin | undefined = await renewSession().catch(() => undefined);
            if (loginResult === undefined) {
                redirectToLogin();
                return Promise.reject(error);
            }
            setToken({ accessToken: loginResult.accessToken, refreshToken: loginResult.refreshToken });
            originalRequest.headers["Authorization"] = `Bearer ${loginResult.accessToken}`;
            return http.request(originalRequest);
        }

        return Promise.reject(error);
    }
);

export default http;
